// 10 modelos de certificados predefinidos

export interface TemplateConfig {
  primaryColor: string
  secondaryColor: string
  accentColor: string
  backgroundColor: string
  textColor: string
  borderStyle: 'classic' | 'modern' | 'double' | 'ornate' | 'minimal' | 'thick' | 'elegant' | 'art-deco' | 'ribbon' | 'none'
  showFlag: boolean
  showSeal: boolean
  showCornerDecorations: boolean
  headerText: string
  titleText: string
  subtitleText: string
  achievementPlaceholder: string
  footerText: string
  fontFamily: string
}

export interface TemplatePreset {
  id: string
  name: string
  description: string
  emoji: string
  config: TemplateConfig
}

export const DEFAULT_CONFIG: TemplateConfig = {
  primaryColor: '#0055A4',
  secondaryColor: '#EF4135',
  accentColor: '#d4af37',
  backgroundColor: '#ffffff',
  textColor: '#1f2937',
  borderStyle: 'classic',
  showFlag: true,
  showSeal: true,
  showCornerDecorations: true,
  headerText: 'NOMADLEXIS',
  titleText: 'CERTIFICADO',
  subtitleText: 'Este certificado se otorga a',
  achievementPlaceholder: 'Unidad 1 - Primeros Pasos en Francés',
  footerText: 'del Curso Intensivo de Francés Nivel A1',
  fontFamily: 'Georgia, serif',
}

export const TEMPLATE_PRESETS: TemplatePreset[] = [
  {
    id: 'classique',
    name: 'Classique Francais',
    description: 'Estilo clásico francés con bordes ornamentados y bandera tricolor',
    emoji: '🇫🇷',
    config: {
      ...DEFAULT_CONFIG,
    },
  },
  {
    id: 'moderne',
    name: 'Moderne Minimal',
    description: 'Diseno limpio y moderno con tipografia grande',
    emoji: '✨',
    config: {
      ...DEFAULT_CONFIG,
      primaryColor: '#0d9488',
      secondaryColor: '#115e59',
      accentColor: '#0d9488',
      borderStyle: 'modern',
      showFlag: false,
      showCornerDecorations: false,
      fontFamily: 'Helvetica, Arial, sans-serif',
    },
  },
  {
    id: 'dore',
    name: 'Or et Prestige',
    description: 'Elegancia dorada con acabado premium',
    emoji: '👑',
    config: {
      ...DEFAULT_CONFIG,
      primaryColor: '#92400e',
      secondaryColor: '#d4af37',
      accentColor: '#d4af37',
      backgroundColor: '#fffbeb',
      borderStyle: 'elegant',
      showFlag: false,
    },
  },
  {
    id: 'academique',
    name: 'Academique Royal',
    description: 'Estilo universitario formal con tonos purpura',
    emoji: '🎓',
    config: {
      ...DEFAULT_CONFIG,
      primaryColor: '#581c87',
      secondaryColor: '#7c3aed',
      accentColor: '#a855f7',
      borderStyle: 'double',
      showFlag: false,
    },
  },
  {
    id: 'tricolore',
    name: 'Tricolore Audacieux',
    description: 'Colores de la bandera francesa en forma audaz',
    emoji: '🏅',
    config: {
      ...DEFAULT_CONFIG,
      primaryColor: '#0055A4',
      secondaryColor: '#EF4135',
      accentColor: '#0055A4',
      borderStyle: 'thick',
      showFlag: true,
      showCornerDecorations: true,
    },
  },
  {
    id: 'elegant',
    name: 'Elegant Parisien',
    description: 'Estilo parisino sutil con tonos rosa',
    emoji: '🌸',
    config: {
      ...DEFAULT_CONFIG,
      primaryColor: '#be185d',
      secondaryColor: '#f9a8d4',
      accentColor: '#ec4899',
      backgroundColor: '#fdf2f8',
      borderStyle: 'minimal',
      showFlag: false,
      showCornerDecorations: false,
      fontFamily: 'Georgia, serif',
    },
  },
  {
    id: 'nature',
    name: 'Nature Provence',
    description: 'Tonos verdes naturales inspirados en la Provenza',
    emoji: '🌿',
    config: {
      ...DEFAULT_CONFIG,
      primaryColor: '#166534',
      secondaryColor: '#4ade80',
      accentColor: '#166534',
      backgroundColor: '#f0fdf4',
      borderStyle: 'ribbon',
      showFlag: false,
    },
  },
  {
    id: 'art-deco',
    name: 'Art Deco',
    description: 'Estilo geometrico Art Deco en negro y dorado',
    emoji: '🔷',
    config: {
      ...DEFAULT_CONFIG,
      primaryColor: '#1c1917',
      secondaryColor: '#d4af37',
      accentColor: '#d4af37',
      backgroundColor: '#fafaf9',
      textColor: '#1c1917',
      borderStyle: 'art-deco',
      showFlag: false,
      showCornerDecorations: true,
    },
  },
  {
    id: 'professionnel',
    name: 'Professionnel',
    description: 'Estilo corporativo limpio y profesional',
    emoji: '💼',
    config: {
      ...DEFAULT_CONFIG,
      primaryColor: '#1e3a5f',
      secondaryColor: '#64748b',
      accentColor: '#1e3a5f',
      borderStyle: 'classic',
      showFlag: false,
      showCornerDecorations: true,
      fontFamily: 'Helvetica, Arial, sans-serif',
    },
  },
  {
    id: 'celebration',
    name: 'Celebration',
    description: 'Estilo festivo y vibrante con estrellas',
    emoji: '🎉',
    config: {
      ...DEFAULT_CONFIG,
      primaryColor: '#7c3aed',
      secondaryColor: '#f59e0b',
      accentColor: '#f59e0b',
      backgroundColor: '#faf5ff',
      borderStyle: 'modern',
      showFlag: false,
      showSeal: true,
      showCornerDecorations: true,
    },
  },
]

export const BORDER_STYLES = [
  { value: 'classic', label: 'Clasico' },
  { value: 'modern', label: 'Moderno' },
  { value: 'double', label: 'Doble linea' },
  { value: 'ornate', label: 'Ornamentado' },
  { value: 'minimal', label: 'Minimal' },
  { value: 'thick', label: 'Grueso' },
  { value: 'elegant', label: 'Elegante' },
  { value: 'art-deco', label: 'Art Deco' },
  { value: 'ribbon', label: 'Cinta' },
  { value: 'none', label: 'Sin borde' },
]

export const FONT_OPTIONS = [
  { value: 'Georgia, serif', label: 'Georgia (Serif)' },
  { value: 'Helvetica, Arial, sans-serif', label: 'Helvetica (Sans)' },
  { value: 'Palatino Linotype, serif', label: 'Palatino (Serif)' },
  { value: 'Garamond, serif', label: 'Garamond (Serif)' },
  { value: 'Trebuchet MS, sans-serif', label: 'Trebuchet (Sans)' },
]
